import { c as _c } from "react/compiler-runtime";
import { getBottomRight, getTopLeft } from 'ol/extent';
import WebMercatorViewport from 'viewport-mercator-project';
import { toast } from 'react-toastify';
import i18n from '@services/next-i18n';
import * as turf from '@turf/turf';
import { IFeature } from '@stores/FeatureStore';
import { polygon } from '@turf/turf';
import { CRSType, DEFAULT_CRS, LFMapGeoJSON, LFMapOptions, SupportedSoilTextures } from './mapTypes';
import { isPresent } from '@farmai-shared/LFUtils';
import { MutableRefObject, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Channel, createConsumer } from '@rails/actioncable';
import { RootStoreContext } from '@stores/rootStore';
import { useQueryOrg } from '@services/useQueryOrg';
import { IVI, VI } from '@stores/VI';
import { SupportedMapLayers } from '@stores/enums';
import { DateTime } from 'luxon';
import { CropProgressResponse, CropzoneResponse, VIAPIResponse } from '@services/responseTypes';
import { Transformers } from '@services/transformer';
import { Fill, Stroke, Style, Text } from 'ol/style';
import CircleStyle from 'ol/style/Circle';
import { Coordinate } from 'ol/coordinate';
import { Geometry, Point, Polygon } from 'ol/geom';
import { Feature, Overlay, Map as OLMap } from 'ol';
import VectorSource from 'ol/source/Vector';
import { IMapStore } from '@stores/MapStore';
const applyToArray = (func, array) => func.apply(Math, array);
export const getBoundsForPolygon = (feature: LFMapGeoJSON | undefined, defaultLocation: {
  center: number[];
  zoom: number;
} = {
  center: [100.523186, 13.736717],
  zoom: 1
} // Thailand
) => {
  if (!feature || feature.geometry?.type !== 'Polygon' || !isPresent(feature.geometry.coordinates)) {
    return defaultLocation;
  }
  // Calculate corner values of bounds of outer ring
  const polygonLong = feature.geometry.coordinates[0]?.map(x => x![0]);
  const polygonLat = feature.geometry.coordinates[0]?.map(x => x![1]);
  const cornersLonLat = [[applyToArray(Math.min, polygonLong), applyToArray(Math.min, polygonLat)], [applyToArray(Math.max, polygonLong), applyToArray(Math.max, polygonLat)]] as [[number, number], [number, number]];

  // Use WebMercatorViewport to get center longitude/latitude and zoom
  const viewport = new WebMercatorViewport({
    width: 600,
    height: 600
  }).fitBounds(cornersLonLat, {
    padding: {
      top: 100,
      bottom: 150,
      left: 100,
      right: 150
    }
  });
  const {
    longitude,
    latitude,
    zoom
  } = viewport;
  const center = [longitude, latitude];
  return {
    center,
    zoom
  };
};
export const getPosition = (handleLocation: (coords: number[], options?: {
  query?: string;
  withDirection?: boolean;
  withFocus?: boolean;
}) => void, options?: {
  query?: string;
  withDirection?: boolean;
  withFocus?: boolean;
}) => navigator.geolocation.getCurrentPosition((position: GeolocationPosition) => {
  handleLocation([position.coords.longitude, position.coords.latitude], options);
}, e => {
  if (options && e.code === 1) {
    handleLocation([], options);
  } else {
    const text = i18n.t('Cannot get current location');
    toast.error(text);
  }
}, {
  maximumAge: 1000 * 5,
  enableHighAccuracy: true,
  timeout: 6000
});
export const getPositionAsync = (options?: {
  query?: string;
  withDirection?: boolean;
}): Promise<[number, number]> => new Promise((resolve, reject) => {
  navigator.geolocation.getCurrentPosition(position => {
    resolve([position.coords.longitude, position.coords.latitude]);
  }, error => {
    if (options && error.code === 1) {
      resolve([] as unknown as [number, number]);
    } else {
      reject(error);
    }
  }, {
    maximumAge: 1000 * 5,
    enableHighAccuracy: true,
    timeout: 6000
  });
});
export const checkNavPermissions = () => navigator?.permissions?.query({
  name: 'geolocation'
}).then(permissionStatus => permissionStatus.state);
const soilStyleMap: Record<SupportedSoilTextures, {
  strokeColor: string;
  fillColor: string;
  textBackground: string;
}> = {
  sandy: {
    strokeColor: 'rgb(243,225,199)',
    fillColor: 'rgba(243,225,199, 0.3)',
    textBackground: '#c2b390'
  },
  sandy_loam: {
    strokeColor: 'rgb(206,191,163)',
    fillColor: 'rgba(206,191,163, 0.3)',
    textBackground: '#c2b390'
  },
  sandy_clay_loam: {
    strokeColor: 'rgb(226,190,139)',
    fillColor: 'rgba(226,190,139, 0.3)',
    textBackground: '#c2b390'
  },
  sandy_clay: {
    strokeColor: 'rgb(224,172,83)',
    fillColor: 'rgba(224,172,83, 0.3)',
    textBackground: '#c2b390'
  },
  loamy_sand: {
    strokeColor: 'rgb(221,205,190)',
    fillColor: 'rgba(221,205,190, 0.3)',
    textBackground: '#c2b390'
  },
  loam: {
    strokeColor: 'rgb(195,167,128)',
    fillColor: 'rgba(195,167,128, 0.3)',
    textBackground: '#c2b390'
  },
  clay_loam: {
    strokeColor: 'rgb(207,166,105)',
    fillColor: 'rgb(207,166,105, 0.3)',
    textBackground: '#c2b390'
  },
  clay: {
    strokeColor: '#CA8200',
    fillColor: 'rgba(202, 130, 0, 0.3)',
    textBackground: '#c2b390'
  },
  silty_clay: {
    strokeColor: 'rgb(183,132,52)',
    fillColor: 'rgba(183,132,52, 0.3)',
    textBackground: '#c2b390'
  },
  silty_clay_loam: {
    strokeColor: 'rgb(172,143,97)',
    fillColor: 'rgba(172,143,97, 0.3)',
    textBackground: '#c2b390'
  },
  silty_loam: {
    strokeColor: 'rgb(157,141,110)',
    fillColor: 'rgba(157,141,110, 0.3)',
    textBackground: '#c2b390'
  },
  silt: {
    strokeColor: 'rgb(114,98,75)',
    fillColor: 'rgba(114,98,75, 0.3)',
    textBackground: '#c2b390'
  },
  no_data: {
    strokeColor: '#fff',
    fillColor: 'rgba(255,255,255, 0.3)',
    textBackground: '#c2b390'
  },
  other: {
    strokeColor: '#000',
    fillColor: 'rgba(0, 0, 0, 0.3)',
    textBackground: '#c2b390'
  }
};
export const geoJSONStyles = (soilTexture: SupportedSoilTextures | undefined, archived = false, withVI = false, customColor?: string) => {
  if (customColor) {
    return {
      strokeColor: '#fff',
      fillColor: customColor,
      strokeWidth: 2
    };
  }
  if (archived) {
    return {
      strokeColor: '#E5E9F3',
      fillColor: 'rgba(114,125,159, 0.5)',
      textBackground: '#c2b390',
      strokeWidth: 5
    };
  }
  if (withVI) {
    return {
      strokeColor: '#2CD1A0',
      fillColor: 'transparent',
      textBackground: '#c2b390',
      strokeWidth: 5
    };
  }
  const base = soilTexture ? soilStyleMap[soilTexture] : undefined;
  return {
    strokeColor: base?.strokeColor ?? '#2CD1A0',
    fillColor: base?.fillColor ?? 'rgba(179, 238, 221, 0.3)',
    textBackground: base?.textBackground ?? '#c2b390',
    strokeWidth: 5
  };
};
export const getBboxFromExtent = extent => {
  const cleanCoord = x => x > 180 ? 180 : x;
  const cleanCoords = coords => coords.map(cleanCoord);
  const bbox = extent && [cleanCoords(getTopLeft(extent)), cleanCoords(getBottomRight(extent))];
  return bbox;
};
export const getBboxFromPolygon = (coords: number[][][], pct?: number) => {
  const polygon = turf.polygon(coords);
  const bbox = turf.bbox(polygon);
  if (pct) {
    return addPaddingToBbox([[bbox[0], bbox[1]], [bbox[2], bbox[3]]], pct);
  }
  return [[bbox[0], bbox[1]], [bbox[2], bbox[3]]];
};
export const addPaddingToBbox = (bbox: number[][], pct: number) => {
  const minLat = bbox[0][0];
  const minLon = bbox[0][1];
  const maxLat = bbox[1][0];
  const maxLon = bbox[1][1];
  const lonPad = (maxLon - minLon) * pct;
  const latPad = (maxLat - minLat) * pct;
  return [[minLat - latPad, minLon - lonPad], [maxLat + latPad, maxLon + lonPad]];
};

// area in m2 by / 1000000 to get km2 / 1080 for ~ polycount sqrt for km
export const calcGridCellSide = (area: number) => Math.sqrt(area / 1000000 / 1080);
export const generatePolygonGrid = (feature: IFeature, options?: {
  properties: {
    [key: string]: string | number | boolean;
  };
}) => {
  if (!feature.getPolygon?.coordinates) return {
    features: []
  };
  const poly = polygon(feature.getPolygon.coordinates);
  const cellSide = calcGridCellSide(feature.fieldArea as number);
  const buffer = turf.buffer(poly, 0.02);
  if (!buffer) return {
    features: []
  };
  const grid = turf.squareGrid(turf.bbox(buffer), cellSide, {
    mask: poly,
    ...options
  });
  return grid;
};
const prefixLength = 'https://'.length;
const consumer = createConsumer(`wss://${process.env.NEXT_PUBLIC_AGROAPI_URL?.replace(/^https?:\/\//, '')}/cable`);
const convertResponseToGeoJSON = (data: CropzoneResponse, hasPlanetSupport = false): LFMapGeoJSON => ({
  geometry: {
    type: 'Polygon',
    coordinates: data.location.boundary.coordinates
  },
  properties: {
    key: `polygon_${data.id}`,
    url: `/monitoring/${data.id}`,
    centroid: data.location.centroid.coordinates,
    name: data.name,
    cropzoneID: data.id,
    scaleTargetId: `map-scale-${data.id}`,
    crs: DEFAULT_CRS as CRSType,
    cursor: 'pointer',
    area: data.area,
    farm: data.farm,
    crop: data.crop,
    plantingDate: data.planting_date,
    ...(data.next_cropzone && {
      nextCropzoneId: data.next_cropzone.id
    }),
    ...(data.archived_at && {
      archivedAt: DateTime.fromISO(data.archived_at)
    }),
    latestVi: data.latest_vis.reduce((acc, vi) => {
      // On the overview map NDVI/EVI for planet orgs are expected to come from a
      // standalone PLANET row, while NDMI always comes from the S2multi multiband
      // row. A standalone row only counts as usable if it actually carries a
      // raster (uri) to render — PLANET rows can exist with uri === null before
      // their imagery is processed. Checking mere existence (the previous
      // behaviour) wrongly suppressed the S2multi fallback and left NDVI/EVI
      // blank while NDMI rendered. Requiring a uri mirrors the single-field
      // getVi behaviour, which falls back to S2multi when PLANET has no data.
      const hasRenderableStandaloneVi = (type: VIAPIResponse) => data.latest_vis.some(v => v.type === type && v.uri);
      const transformedVis = vi.type === VIAPIResponse.S2MULTI ? [Transformers.multibandVi(vi, SupportedMapLayers.NDMI), (!hasPlanetSupport || !hasRenderableStandaloneVi(VIAPIResponse.NDVI)) && Transformers.multibandVi(vi, SupportedMapLayers.NDVI), (!hasPlanetSupport || !hasRenderableStandaloneVi(VIAPIResponse.EVI)) && Transformers.multibandVi(vi, SupportedMapLayers.EVI)].filter(Boolean) : [Transformers.vi(vi)];
      transformedVis.forEach(transformedVi => acc.push(VI.create(transformedVi)));
      return acc;
    }, [] as IVI[]),
    fieldID: data.field.id || undefined,
    yieldColor: getColorForYield(isPresent(data.actual?.yield) ? parseFloat(data.actual.yield) : null, data.crop.name, data.crop.canonical_name),
    cropProgress: (() => {
      if (data.actual?.harvesting_date) {
        const harvestDT = DateTime.fromISO(data.actual.harvesting_date);
        if (harvestDT.isValid && harvestDT.toISODate()! <= DateTime.now().toISODate()!) {
          return 'harvested';
        }
      }
      return data.crop_progress || null;
    })(),
    gdd: data.gdd,
    predicted: {
      maturityDate: data?.predicted?.maturity_date
    },
    createdAt: data.created_at,
    actual: {
      harvest: data.actual?.harvest ? parseFloat(data.actual.harvest) : null,
      yield: data.actual?.yield ? parseFloat(data.actual.yield) : null,
      harvestingDate: data.actual?.harvesting_date ? data.actual.harvesting_date : null
    }
  }
});
const isVIType = (mapLayer: SupportedMapLayers) => mapLayer === SupportedMapLayers.NDVI || mapLayer === SupportedMapLayers.NDMI || mapLayer === SupportedMapLayers.EVI;
export type VscStageEntry = {
  cropProgress: CropProgressResponse | null;
  gdd: {
    current_gdd: number;
    target_gdd: number;
    percentage: string;
    is_matured: boolean;
  } | null;
  archivedAt: unknown;
};
export const useVscCropzoneStageData = t0 => {
  const $ = _c(6);
  const {
    enabled
  } = t0;
  const {
    uiStore
  } = useContext(RootStoreContext);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["vsc-cropzone-stage-data"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== uiStore) {
    t2 = async () => {
      const result = new Map();
      const seen = new Set();
      for (let page = 1; page <= 100; page = page + 1, page) {
        const response = await uiStore.currentOrganization.fetchCropzones({
          page,
          items: 200
        });
        if (!response || response.length === 0) {
          break;
        }
        let added = 0;
        for (const c of response) {
          const id = (c as any).properties?.id ?? (c as any).id;
          if (!id || seen.has(id)) {
            continue;
          }
          seen.add(id);
          result.set(id, {
            cropProgress: (c as any).properties?.cropProgress ?? null,
            gdd: (c as any).properties?.gdd ?? null,
            archivedAt: (c as any).properties?.archivedAt
          });
          added = added + 1;
        }
        if (added === 0 || response.length < 200) {
          break;
        }
      }
      return result;
    };
    $[1] = uiStore;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== enabled || $[4] !== t2) {
    t3 = {
      queryKey: t1,
      queryFn: t2,
      enabled,
      refetchOnWindowFocus: false
    };
    $[3] = enabled;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return useQueryOrg(t3);
};
export const useCropzoneSocket = t0 => {
  const $ = _c(33);
  const {
    enabled: t1,
    isArchived: t2,
    needsLatestVis: t3,
    mapLayer,
    batchSize: t4
  } = t0;
  const enabled = t1 === undefined ? false : t1;
  const isArchived = t2 === undefined ? false : t2;
  const needsLatestVis = t3 === undefined ? false : t3;
  const batchSize = t4 === undefined ? 1000 : t4;
  const {
    userStore,
    uiStore
  } = useContext(RootStoreContext);
  let t5;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = new Map();
    $[0] = t5;
  } else {
    t5 = $[0];
  }
  const [data, setData] = useState(t5);
  const [isConnected, setConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const subscriptionRef = useRef(null);
  const isLoadingRef = useRef(true);
  const [cachedStatus, setCachedStatus] = useState(isArchived);
  if (cachedStatus !== isArchived) {
    setCachedStatus(isArchived);
    setData(new Map());
  }
  let t6;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = (action, message) => {
      if (subscriptionRef.current) {
        subscriptionRef.current.perform(action, message);
      }
    };
    $[1] = t6;
  } else {
    t6 = $[1];
  }
  const sendMessage = t6;
  let t7;
  if ($[2] !== batchSize || $[3] !== isArchived || $[4] !== isConnected || $[5] !== needsLatestVis || $[6] !== uiStore.currentOrganization || $[7] !== uiStore.lfmap.socketBbox) {
    t7 = () => {
      if (isPresent(uiStore.lfmap.socketBbox) && isConnected && uiStore.currentOrganization) {
        isLoadingRef.current = true;
        sendMessage("boundary_cropzones", {
          organization_id: uiStore.currentOrganization.id,
          latest_vis: needsLatestVis,
          boundary: uiStore.lfmap.socketBbox,
          items: batchSize,
          filter: {
            status: isArchived ? "archived" : "current"
          }
        });
      }
    };
    $[2] = batchSize;
    $[3] = isArchived;
    $[4] = isConnected;
    $[5] = needsLatestVis;
    $[6] = uiStore.currentOrganization;
    $[7] = uiStore.lfmap.socketBbox;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== isArchived || $[10] !== isConnected || $[11] !== uiStore.lfmap.socketBboxFlatString) {
    t8 = [uiStore.lfmap.socketBboxFlatString, isConnected, isArchived];
    $[9] = isArchived;
    $[10] = isConnected;
    $[11] = uiStore.lfmap.socketBboxFlatString;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  useEffect(t7, t8);
  let t9;
  if ($[13] !== mapLayer || $[14] !== needsLatestVis || $[15] !== uiStore.lfmap) {
    t9 = () => {
      if (needsLatestVis && isVIType(mapLayer)) {
        uiStore.lfmap.copyBboxToSocketBbox();
      }
    };
    $[13] = mapLayer;
    $[14] = needsLatestVis;
    $[15] = uiStore.lfmap;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== mapLayer || $[18] !== needsLatestVis) {
    t10 = [needsLatestVis, mapLayer];
    $[17] = mapLayer;
    $[18] = needsLatestVis;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  useEffect(t9, t10);
  let t11;
  if ($[20] !== batchSize || $[21] !== enabled || $[22] !== uiStore.currentOrganization?.hasPlanetSupport || $[23] !== userStore) {
    t11 = () => {
      if (!enabled || subscriptionRef.current || !userStore.agroApiToken?.access_token) {
        return;
      }
      const subscription = consumer.subscriptions.create({
        channel: "CropzonesChannel",
        token: userStore.agroApiToken.access_token
      }, {
        initialized() {
          console.log("Initialised Action Cable");
        },
        connected() {
          console.log("Connected to action cable server");
          setConnected(true);
        },
        received(message_0) {
          const batch = JSON.parse(message_0);
          const isFinalBatch = batch.length < batchSize;
          setData(prevData => {
            const newData = new Map(prevData);
            batch.forEach(c => {
              const cz = convertResponseToGeoJSON(c, uiStore.currentOrganization?.hasPlanetSupport);
              if (cz.properties.cropzoneID) {
                newData.set(cz.properties.cropzoneID, cz);
              }
            });
            return newData;
          });
          if (isFinalBatch) {
            setIsLoading(false);
          }
        }
      });
      subscriptionRef.current = subscription;
      return () => {
        if (subscription) {
          setConnected(false);
          subscription.unsubscribe();
          consumer.disconnect();
          subscriptionRef.current = null;
        }
      };
    };
    $[20] = batchSize;
    $[21] = enabled;
    $[22] = uiStore.currentOrganization?.hasPlanetSupport;
    $[23] = userStore;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== enabled) {
    t12 = [enabled];
    $[25] = enabled;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  useEffect(t11, t12);
  let t13;
  if ($[27] !== data || $[28] !== isArchived) {
    t13 = Array.from(data.values()).filter(cz_0 => isPresent(cz_0.properties.archivedAt) === isArchived);
    $[27] = data;
    $[28] = isArchived;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== isLoading || $[31] !== t13) {
    t14 = {
      data: t13,
      isLoading
    };
    $[30] = isLoading;
    $[31] = t13;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  return t14;
};
export const createStripedPattern = (lineWidth: number, spacing: number, slope: number, strokeColor: string, fillColor: string, opacity = 1) => {
  if (typeof window === 'undefined') return;
  const can = document.createElement('canvas');
  const len = Math.hypot(1, slope);
  const w = can.width = 1 / len + spacing + 0.5 | 0; // round to nearest pixel
  const h = can.height = slope / len + spacing * slope + 0.5 | 0;
  const ctx = can.getContext('2d');
  if (ctx) {
    ctx.resetTransform();
    ctx.globalAlpha = opacity;
    ctx.strokeStyle = strokeColor;
    ctx.lineWidth = lineWidth;
    ctx.fillStyle = fillColor;
    ctx.beginPath();

    // Line through top left and bottom right corners
    ctx.moveTo(0, 0);
    ctx.lineTo(-w, h);
    // Line through top right corner to add missing pixels
    ctx.moveTo(0, -h);
    ctx.lineTo(-w / 2, h);
    // Line through bottom left corner to add missing pixels
    ctx.moveTo(w, 0);
    ctx.lineTo(-w, h * 2);
    ctx.fillRect(0, 0, w, h);
    ctx.stroke();
    return ctx.createPattern(can, 'repeat');
  }
  return null;
};
export const POLYGON_STROKE_COLOR = '#2CD1A0';
export const POLYGON_FILL_COLOR = createStripedPattern(1, 24, 1, 'rgba(44,209,160, 0.7)', 'rgba(44,209,160, 0.3)');
export const OUT_OF_BOUNDARY_STROKE_COLOR = '#ff2525';
export const OUT_OF_BOUNDARY_FILL_COLOR = createStripedPattern(1, 24, 1, 'rgba(255,37,37, 0.7)', 'rgba(255,37,37, 0.3)', 0.5);
export const LINE_STYLE = new Style({
  stroke: new Stroke({
    color: POLYGON_STROKE_COLOR,
    width: 4
  })
});
export const POLYGON_FILL = new Fill({
  color: POLYGON_FILL_COLOR
});
export const POLYGON_STROKE = new Stroke({
  color: POLYGON_STROKE_COLOR,
  width: 4
});
export const createVertexMarkerStyle = (coordinate: Coordinate, index: number, radius: number) => new Style({
  image: new CircleStyle({
    radius: radius,
    fill: new Fill({
      color: '#fff'
    })
  }),
  text: new Text({
    text: (index + 1).toString(),
    fill: new Fill({
      color: '#000'
    })
  }),
  geometry: new Point(coordinate)
});
export const BACKGROUND_FIELD_COLOR = createStripedPattern(1, 4, 4, 'rgba(114, 125, 159, 0.7)', 'rgba(114, 125, 159, 0.3)', 0.4);
export const BACKGROUND_FIELD_STROKE_COLOR = '#727d9f';
export const BACKGROUND_FIELD_STYLE = new Style({
  fill: new Fill({
    color: BACKGROUND_FIELD_COLOR
  }),
  stroke: new Stroke({
    color: BACKGROUND_FIELD_STROKE_COLOR,
    width: 2
  })
});
export const checkPointWithinCircle = (center: Coordinate, markerCoord: number[] | undefined, resolution: number | undefined) => {
  if (!isPresent(resolution) || !isPresent(markerCoord)) return false;
  const radiusInMeters = 6 * resolution;
  return turf.booleanPointInPolygon(turf.point(center), turf.circle(markerCoord, radiusInMeters, {
    units: 'meters'
  }));
};
export const createFeature = <T extends Geometry,>(geometry: T, properties: Record<string, any> = {}): Feature<T> => new Feature<T>({
  geometry,
  properties
});

// SIMILAR EDIT FUNCTIONS

const MAP_CACHE = {
  fillStyles: {},
  strokeStyles: {},
  selectStyles: {},
  styles: {}
};
export const markerStyleFunction = feature => {
  const properties = feature.get('properties');
  if (!properties?.cacheKey) return;
  return MAP_CACHE.styles[properties.cacheKey] ||= new Style({
    image: new CircleStyle({
      radius: 10,
      fill: new Fill({
        color: '#fff'
      }),
      stroke: new Stroke({
        color: POLYGON_STROKE_COLOR,
        width: 1
      })
    }),
    zIndex: properties.zIndex || 1,
    text: properties.index !== undefined ? new Text({
      text: (properties.index + 1).toString(),
      fill: new Fill({
        color: '#000'
      })
    }) : undefined
  });
};

// style of feature when selected
export const markerSelectStyleFunction = feature => {
  const properties = feature.get('properties');
  if (!properties?.cacheKey) return;
  return MAP_CACHE.selectStyles[properties.cacheKey] ||= new Style({
    image: new CircleStyle({
      radius: 10,
      fill: new Fill({
        color: '#000'
      }),
      stroke: new Stroke({
        color: POLYGON_STROKE_COLOR,
        width: 2
      })
    }),
    zIndex: properties.zIndex || 1,
    text: properties.index !== undefined ? new Text({
      text: (properties.index + 1).toString(),
      fill: new Fill({
        color: '#fff'
      })
    }) : undefined
  });
};
const POLYGON_STYLE = new Style({
  fill: POLYGON_FILL,
  stroke: POLYGON_STROKE,
  zIndex: 100
});
const OUT_OF_BOUNDS_STYLE = new Style({
  fill: new Fill({
    color: OUT_OF_BOUNDARY_FILL_COLOR
  }),
  stroke: new Stroke({
    color: OUT_OF_BOUNDARY_STROKE_COLOR,
    width: 2
  }),
  zIndex: 100
});
export const updatePolygonFeature = (feature: IFeature, polygonFeatureRef: MutableRefObject<Feature<Polygon> | null>, options: LFMapOptions, source: VectorSource) => {
  if (!feature) return;
  const coordinates = feature.geometry.getPolygon?.openOuterCoords;
  if (!isPresent(coordinates)) return;
  if (!polygonFeatureRef.current) {
    polygonFeatureRef.current = new Feature(new Polygon([coordinates]));
    polygonFeatureRef.current.setStyle(options?.withFieldBoundary ? OUT_OF_BOUNDS_STYLE : POLYGON_STYLE);
    polygonFeatureRef.current.setId('editable-polygon');
    source.addFeature(polygonFeatureRef.current);
  } else {
    polygonFeatureRef.current.getGeometry()?.setCoordinates([coordinates]);
  }
};
export const updateMarkerFeatures = (feature: IFeature, markerFeaturesRef: MutableRefObject<Feature<Point>[]>, source: VectorSource) => {
  if (!feature) return;
  const coordinates = feature.geometry.getPolygon?.openOuterCoords;
  if (!isPresent(coordinates)) return;
  markerFeaturesRef.current.forEach(marker => source.removeFeature(marker));
  markerFeaturesRef.current = coordinates.map((coord, idx) => {
    const markerFeature = new Feature({
      geometry: new Point(coord),
      properties: {
        strokeColor: '#fff',
        strokeWidth: '2',
        selectedColor: '#000',
        fillColor: '#fff',
        cursor: 'pointer',
        index: idx,
        cacheKey: `circle-feature-${idx}`,
        zIndex: 1000
      }
    });
    markerFeature.setStyle(markerStyleFunction);
    source.addFeature(markerFeature);
    return markerFeature;
  });
};
export const clearSelection = (lfmap: IMapStore, overlayRef: MutableRefObject<Overlay | null>) => {
  lfmap.setSelectedMarker(null);
  overlayRef.current?.setPosition(undefined);
};
export const handleClear = (marker: Feature<Point>, feature: IFeature, polygonFeatureRef: MutableRefObject<Feature<Polygon> | null>, options: LFMapOptions, source: VectorSource, markerFeaturesRef: MutableRefObject<Feature<Point>[]>, lfmap: IMapStore, overlayRef: MutableRefObject<Overlay | null>) => {
  const clearedMarkerIndex = marker.get('properties').index as number;
  const polygon = feature?.getPolygon;
  if (!isPresent(clearedMarkerIndex) || !isPresent(polygon)) return;
  const updatedCoordinates = polygon.outerCoords?.filter((coord, idx) => clearedMarkerIndex !== idx);
  if (updatedCoordinates) {
    polygon.replaceRing(0, updatedCoordinates);
    updatePolygonFeature(feature, polygonFeatureRef, options, source);
    updateMarkerFeatures(feature, markerFeaturesRef, source);
  }
  clearSelection(lfmap, overlayRef);
};
export const createOverlay = (map: OLMap, popupContainerRef: MutableRefObject<HTMLDivElement | null>, overlayRef: MutableRefObject<Overlay | null>) => {
  if (!map) return;
  const popupElement = document.createElement('div');
  popupElement.id = 'edit-marker-overlay-container';
  document.body.appendChild(popupElement);
  popupContainerRef.current = popupElement;
  const overlay = new Overlay({
    id: 'edit-marker-overlay',
    element: popupElement,
    positioning: 'bottom-center',
    offset: [0, -24],
    stopEvent: true
  });
  map.addOverlay(overlay);
  overlayRef.current = overlay;
};
export const updateCoordinates = (polygonFeatureRef: MutableRefObject<Feature<Polygon> | null>, coords: number[], idx: number) => {
  const polygon = polygonFeatureRef.current?.getGeometry();
  if (!polygon) return;
  const coordinates = polygon.getCoordinates()?.[0];
  if (coordinates) {
    coordinates[idx] = coords;
  }
  return coordinates;
};
export const handleMapUpdates = (map: OLMap, lfmap: IMapStore, source: VectorSource, polygonFeatureRef: MutableRefObject<Feature<Polygon> | null>, updateCropzoneIntersection: (source: VectorSource) => void, coord?: number[]) => {
  const updateCoord = coord || map.getView().getCenter() as Coordinate;
  const coordinate = lfmap.selectedMarker?.getGeometry()?.getCoordinates();
  if (!map || !isPresent(coordinate)) return;
  const features = source.getFeaturesAtCoordinate(coordinate);
  const selectedMarker = features.find(f => f.getGeometry() instanceof Point);
  if (!selectedMarker) return;
  const coordinates = updateCoordinates(polygonFeatureRef, updateCoord, selectedMarker.get('properties').index);
  lfmap.selectedMarker?.getGeometry()?.setCoordinates(updateCoord);
  if (!isPresent(coordinates)) return;
  polygonFeatureRef.current?.getGeometry()?.setCoordinates([coordinates]);
  updateCropzoneIntersection(source);
};
export const handleAddPoint = (map: OLMap, feature: IFeature, polygonFeatureRef: MutableRefObject<Feature<Polygon> | null>, markerFeaturesRef: MutableRefObject<Feature<Point>[]>, options: LFMapOptions, source: VectorSource, geo?: Point) => {
  const coordinate = geo?.getCoordinates() || map.getView().getCenter();
  const polygon = feature.getPolygon;
  if (!polygon || !isPresent(coordinate)) return;
  const coordinates = polygon.outerCoords;
  const boundary = turf.lineString(coordinates);
  const clickedPoint = turf.point(coordinate);
  const nearestPoint = turf.nearestPointOnLine(boundary, clickedPoint);
  if (!nearestPoint) return;
  const nearestCoords = nearestPoint.geometry.coordinates;
  const segmentIndex = nearestPoint.properties.index;
  coordinates.splice(segmentIndex + 1, 0, nearestCoords);
  feature.getPolygon?.replaceRing(0, coordinates);
  updatePolygonFeature(feature, polygonFeatureRef, options, source);
  updateMarkerFeatures(feature, markerFeaturesRef, source);
};
export const yieldColorStops: [number, [number, number, number]][] = [[0, [45, 0, 75]],
// dark purple
[0.5, [96, 199, 124]],
// green
[1, [255, 255, 102]] // yellow
];
type YieldRange = {
  min: number; // t/m²
  max: number; // t/m²
};
type CropRanges = {
  _crop: YieldRange;
  varieties?: Record<string, YieldRange>; // canonical variety key
};
export const yieldReferenceRanges: Record<string, CropRanges> = {
  sweetcorn: {
    _crop: {
      min: 0.000567497,
      max: 0.00198422
    },
    // mixed fallback
    varieties: {
      'corn_hi-brix_59': {
        min: 0.000567497,
        max: 0.00198422
      },
      corn: {
        min: 0.000576569,
        max: 0.00196551
      } // generic
    }
  },
  rice: {
    _crop: {
      min: 0.000305891,
      max: 0.000846863
    },
    // mixed fallback
    varieties: {
      rice_Koshihikari: {
        min: 0.000354145,
        max: 0.00070631
      },
      rice_jasmine_105: {
        min: 0.000100246,
        max: 0.000295
      } // https://gitlab.com/listenfield/products/farmai/lf-dashboard/-/merge_requests/1508#note_2705381576
    }
  },
  sugarcane: {
    _crop: {
      min: 0.000910992,
      max: 0.0135681
    },
    // mixed fallback
    varieties: {
      sugarcane_kk_3: {
        min: 0.000910992,
        max: 0.0135681
      },
      sugarcane: {
        min: 0.00377442,
        max: 0.00684984
      } // generic
    }
  },
  cassava: {
    _crop: {
      min: 0.0008,
      // Thai cassava yields are generally lower than than average in Asia https://ourworldindata.org/grapher/cassava-yields
      max: 0.004239 // Reducing this to prevent throw off of low thai values
    },
    varieties: {
      cassava_Huaybong_80: {
        min: 0.003103,
        max: 0.006239
      },
      cassava_Huaybong_90: {
        min: 0.00318,
        max: 0.00618 // no evidence, just creating a range.
      }
    }
  }
};
export const getYieldRange = (cropKey: string, varietyKey?: string) => {
  if (!cropKey) return null;
  const crop = yieldReferenceRanges[cropKey.toLowerCase()];
  if (!crop) return null;
  if (varietyKey) {
    const v = crop.varieties?.[varietyKey.toLowerCase()];
    if (v) return v;
  }
  return crop._crop ?? null;
};
export const normalizeYield = (value: number | null, crop: string, variety?: string): number | null => {
  if (value == null || Number.isNaN(value)) return null;
  const range = getYieldRange(crop, variety);
  if (!range) return null;
  const span = range.max - range.min;
  if (span <= 0) return null;
  const clamped = Math.max(range.min, Math.min(range.max, value));
  return (clamped - range.min) / span;
};
export const interpolateColor = (value: number | null, stops: ReadonlyArray<readonly [number, readonly [number, number, number]]> = yieldColorStops): string => {
  if (value == null || Number.isNaN(value)) return 'rgba(255,255,255,0.3)';
  if (value <= stops[0][0]) return `rgb(${stops[0][1].join(',')})`;
  if (value >= stops[stops.length - 1][0]) return `rgb(${stops[stops.length - 1][1].join(',')})`;
  for (let i = 0; i < stops.length - 1; i++) {
    const [v1, c1] = stops[i];
    const [v2, c2] = stops[i + 1];
    if (value >= v1 && value <= v2) {
      const t = (value - v1) / (v2 - v1);
      const r = Math.round(c1[0] + (c2[0] - c1[0]) * t);
      const g = Math.round(c1[1] + (c2[1] - c1[1]) * t);
      const b = Math.round(c1[2] + (c2[2] - c1[2]) * t);
      return `rgb(${r},${g},${b})`;
    }
  }
  return 'rgba(255,255,255,0.3)';
};
export const getColorForYield = (rawYield: number | null, crop: string, variety?: string) => {
  const norm = normalizeYield(rawYield, crop, variety);
  return interpolateColor(norm, yieldColorStops);
};
export const CropProgressColors = {
  created: 'rgb(191, 199, 225)',
  planted: 'rgb(28, 196, 73)',
  ready_to_harvest: 'rgb(214, 177, 10)',
  harvested: 'rgb(168, 104, 97)',
  overdue: 'rgb(251,44,54)'
} as const;
export type CropStage = 'created' | 'planted' | 'ready_to_harvest' | 'resting';
const CropStageColors: Record<CropStage, string> = {
  created: CropProgressColors.created,
  planted: CropProgressColors.planted,
  ready_to_harvest: CropProgressColors.ready_to_harvest,
  resting: CropProgressColors.harvested
};
interface StageResolveOptions {
  archived?: boolean;
  gdd?: {
    percentage?: string;
    is_matured?: boolean;
  } | null;
}
export const resolveCropStage = (progress: CropProgressResponse | null | undefined, options?: StageResolveOptions): CropStage => {
  if (options?.archived) return 'resting';
  if (progress === 'harvested') return 'resting';
  if (progress === 'ready_to_harvest' || progress === 'overdue') {
    return 'ready_to_harvest';
  }
  if (progress === 'planted') {
    const gddPct = parseFloat(options?.gdd?.percentage ?? '0');
    if (options?.gdd?.is_matured || Number.isFinite(gddPct) && gddPct > 90) {
      return 'ready_to_harvest';
    }
    return 'planted';
  }
  return 'created';
};
export const resolveStageProgress = (progress: CropProgressResponse | null | undefined, options?: StageResolveOptions & {
  useStageMapping?: boolean;
}): CropStage | CropProgressResponse | null => {
  if (options?.useStageMapping) return resolveCropStage(progress, options);
  return progress ?? null;
};
export const getColorForCropProgress = (progress: CropProgressResponse | null, options?: StageResolveOptions & {
  useStageMapping?: boolean;
}): string => {
  if (options?.useStageMapping) {
    return CropStageColors[resolveCropStage(progress, options)];
  }
  if (!progress) return 'rgba(255,255,255,0.3)';
  return CropProgressColors[progress] || 'rgba(255,255,255,0.3)';
};